import { graphql, useStaticQuery } from 'gatsby'
import React from 'react'
import { Helmet } from 'react-helmet'

import { useGetFooter } from '@application/Interfaces/Hooks/useGetFooter'
import { useOrganization } from '@application/Interfaces/Hooks/useOrganization/useOrganization'
import { useTranslation } from '@application/Interfaces/Hooks/useTranslation'
import { AlternateUrlType } from '@application/Types/ComponentTypes'
import type { Locale } from '@application/Types/Locale'
import { Nullable } from '@application/Types/Opt'
import { getPageUrl } from '@application/Utils/generalUtils'
import { ensureForwardSlash } from '@infrastructure/Url/ensureForwardSlash'
import { ensureNoForwardSlash } from '@infrastructure/Url/ensureNoForwardSlash'
import { WindowLocation } from '@reach/router'
import { getCompletePath } from '@src/Application/Utils/StringUtility'
import { ensureTrailingSlash } from '@src/Infrastructure/Url/ensureTrailingSlash'

import { getCmsSchameMarkup, getOgImagePath, getOgImageUrl, getSchema } from './utils'

const BASIC_ROBOTS_CONTENT = 'noodp, noydir'
const ROBOTS_INDEX = 'index'
const ROBOTS_FOLLOW = 'follow'
const ROBOTS_NO_INDEX = 'noindex'
const ROBOTS_NO_FOLLOW = 'nofollow'

type Props = {
    title?: Nullable<string>
    description?: Nullable<string>
    isNoFollow?: boolean
    isNoIndex?: boolean
    imageUrl?: string
    location: WindowLocation<unknown>
    canonicalURLOverride?: string
    schemaPrimaryImageOfPageUrl?: string
    lang?: string
    alternateUrls: Nullable<AlternateUrlType[]>
    schemaMarkup?: string
}

const MAP_OG_LOCALE_ALTERNATE: Record<Locale, string> = {
    en: 'en_US',
    es: 'es_ES',
    de: 'de_DE',
    nl: 'nl_NL',
    ar: 'ar_AE',
}

export const Seo = (props: Props): JSX.Element => {
    const {
        title,
        description,
        isNoFollow = false,
        isNoIndex = false,
        imageUrl,
        canonicalURLOverride = null,
        schemaPrimaryImageOfPageUrl,
        lang = 'en',
        location,
        alternateUrls,
        schemaMarkup,
    } = props

    const footer = useGetFooter(lang)
    const { t } = useTranslation()
    const data = useStaticQuery(graphql`
        query {
            site {
                siteMetadata {
                    siteUrl
                    fbAdminIdEn
                    fbAdminIdEs
                }
            }
        }
    `)
    const { siteUrl, fbAdminIdEn, fbAdminIdEs } = data.site.siteMetadata
    const fbAdminIdMap: Record<Locale, string> = {
        en: fbAdminIdEn,
        es: fbAdminIdEs,
        de: fbAdminIdEn,
        nl: fbAdminIdEn,
        ar: fbAdminIdEn,
    }

    const translatedTitle = title ? title : t('metaTitle')
    const translatedDescription = description ? description : t('metaDescription')

    const translatedSiteName = t('metaSiteName')

    const index = isNoIndex ? ROBOTS_NO_INDEX : ROBOTS_INDEX
    const follow = isNoFollow ? ROBOTS_NO_FOLLOW : ROBOTS_FOLLOW

    let robots = `${index}, ${follow}, ${BASIC_ROBOTS_CONTENT}`

    const pageUrl = `${siteUrl}${location.pathname}`

    if (process.env.GATSBY_ACTIVE_ENV !== 'production' || process.env.GATSBY_IS_PREVIEW === 'true') {
        robots = `${ROBOTS_NO_INDEX}, ${ROBOTS_NO_FOLLOW}, ${BASIC_ROBOTS_CONTENT}`
    }

    const telephone =
        footer?.getInTouchItems.reduce((previousValue, currentValue) => {
            return [...previousValue, currentValue.phoneNumber]
        }, [] as string[]) ?? []

    const organization = useOrganization()

    const siteMetadata = organization.siteMetadata[lang as Locale]
    const twitterSite = siteMetadata?.twitterSite
    const ogImageUrlPath = getOgImagePath(siteMetadata?.ogImageUrlPath)
    const ogImageUrl = getOgImageUrl({ imageUrl, ogImageUrlPath, siteUrl })

    const schema = getSchema({
        lang: lang as Locale,
        pageUrl,
        siteUrl,
        title: translatedTitle,
        description: translatedDescription,
        contentUrl: schemaPrimaryImageOfPageUrl || ogImageUrl,
        telephone,
        organizationName: organization.name,
        organizationLogoPath: `${siteUrl}${getCompletePath(organization.logoPath)}`,
        organizationInfoEmail: organization.infoEmailId,
        siteMetadata: siteMetadata,
    })

    const cmsSchemaMarkup = getCmsSchameMarkup(schemaMarkup)

    const faviconIconPath = getCompletePath(organization.faviconIconPath)

    // TODO update logic for canonicals/alternates for subpages based on CMS page existance
    return (
        <Helmet
            htmlAttributes={{
                lang,
            }}
        >
            <title>{translatedTitle}</title>
            <link rel="icon" type="image/x-icon" href={faviconIconPath} />
            <meta name="title" content={translatedTitle} />
            <meta name="description" content={translatedDescription} />
            {ogImageUrl && <meta property="og:image" itemProp="image" content={ogImageUrl} />}
            <meta property="og:title" content={translatedTitle} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={pageUrl} />
            <meta property="fb:admins" content={fbAdminIdMap[lang as Locale]} />
            <meta property="og:site_name" content={translatedSiteName} />
            <meta property="og:description" content={translatedDescription} />
            <meta property="og:locale" content={MAP_OG_LOCALE_ALTERNATE[lang as Locale]} />
            {alternateUrls?.map((a) => (
                <meta
                    property="og:locale:alternate"
                    content={MAP_OG_LOCALE_ALTERNATE[a.language as Locale]}
                    key={a.language}
                />
            ))}
            <meta property="og:updated_time" content="2017-08-11 20:59:55 +0000 UTC" />
            <meta name="twitter:card" content={t('metaTwitterCard')} />
            {twitterSite && <meta name="twitter:site" content={twitterSite} />}
            <meta name="twitter:title" content={translatedTitle} />
            <meta name="twitter:description" content={translatedDescription} />
            {twitterSite && <meta name="twitter:creator" content={twitterSite} />}
            {ogImageUrl && <meta name="twitter:image:src" content={ogImageUrl} />}
            {ogImageUrl && <meta itemProp="image" content={ogImageUrl} />}
            <meta name="robots" content={robots} />

            <link rel="canonical" href={canonicalURLOverride || pageUrl} />
            {alternateUrls?.map((a) => {
                const pageActivePath = getPageUrl(a)
                const activePathWithLocal =
                    a.language === 'en' ? pageActivePath : `${a.language}${ensureForwardSlash(pageActivePath)}`
                const pagePath = getCompletePath(ensureForwardSlash(activePathWithLocal))
                const pageUrl = `${ensureTrailingSlash(siteUrl)}${ensureNoForwardSlash(pagePath)}`
                return (
                    <link rel="alternate" hrefLang={a.language} href={ensureTrailingSlash(pageUrl)} key={a.language} />
                )
            })}

            <script type="application/ld+json">{JSON.stringify(schema)}</script>
            {cmsSchemaMarkup && <script type="application/ld+json">{cmsSchemaMarkup}</script>}
        </Helmet>
    )
}
